/**
 * TRUS-703 (Integrated Inbox, phase 2): the cards a mirrored member-DM message
 * renders as, in the staff thread and the member portal thread.
 *
 *  - campaign:  megaphone · campaign name · SMS/Email chip · first line;
 *               click expands the full text (+ subject for email).
 *  - call:      one line, the TRUS-668 call card's look: Incoming / Outgoing /
 *               Missed call · duration · staff name when known.
 *  - voicemail: duration + transcript (staff only) or "Transcript unavailable".
 *               Members never see a transcript. TRUS-719: staff get a Play
 *               control (authenticated audio endpoint) when the recording is
 *               available, and "Transcribing…" while the transcript is pending.
 *
 * `MemberDmCardView` renders null for anything it does not understand, so the
 * caller falls back to its plain bubble.
 */
import { useId, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Megaphone,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Voicemail as VoicemailIcon,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { formatCallDuration } from '@/services/callsApi';
import { VoicemailPlayer } from './VoicemailPlayer';
import {
  firstLine,
  memberDmCardOf,
  mirroredSourceText,
  type CampaignCard,
  type PhoneCallCard,
  type VoicemailCard,
} from './memberDmCards';

type Tone = 'default' | 'inverted';
type Viewer = 'staff' | 'member';

interface MemberDmCardViewProps {
  metadata: Record<string, any> | null | undefined;
  /** The message body: for a campaign, the text the member received. */
  body: string | null | undefined;
  viewer: Viewer;
  tone?: Tone;
  /** Resolve a staff user id to a display name (staff thread only). */
  nameOf?: (userId: number) => string | undefined;
  /** The message's channel and id: needed for voicemail playback (staff thread only). */
  channelId?: number | null;
  messageId?: number | null;
}

export function MemberDmCardView({ metadata, body, viewer, tone = 'default', nameOf, channelId, messageId }: MemberDmCardViewProps) {
  const card = memberDmCardOf(metadata);
  if (!card) return null;
  switch (card.type) {
    case 'campaign':
      return <CampaignMessageCard card={card} body={body} tone={tone} />;
    case 'call':
      return <PhoneCallMessageCard card={card} tone={tone} viewer={viewer} nameOf={nameOf} />;
    case 'voicemail':
      return <VoicemailMessageCard card={card} tone={tone} viewer={viewer} channelId={channelId} messageId={messageId} />;
    default:
      return null;
  }
}

function IconDisc({ icon: Icon, tone, alert = false }: { icon: typeof Megaphone; tone: Tone; alert?: boolean }) {
  return (
    <div
      className={cn(
        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
        tone === 'inverted'
          ? 'bg-white/20 text-white'
          : alert
            ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
            : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200',
      )}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </div>
  );
}

const muted = (tone: Tone) => (tone === 'inverted' ? 'text-white/80' : 'text-muted-foreground');

export function CampaignMessageCard({ card, body, tone = 'default' }: { card: CampaignCard; body: string | null | undefined; tone?: Tone }) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const inverted = tone === 'inverted';
  const text = body ?? '';
  const preview = firstLine(text);
  const name = card.campaign_name ?? t('messaging.mirror.campaign.untitled', 'Campaign');
  const chip =
    card.delivery_channel === 'email'
      ? t('messaging.replyChannel.email', 'Email')
      : card.delivery_channel === 'sms'
        ? t('messaging.replyChannel.sms', 'SMS')
        : null;
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="campaign-card" data-campaign-id={card.campaign_id ?? undefined}>
      <button
        type="button"
        className="flex min-w-0 items-start gap-3 rounded-md py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={regionId}
        data-testid="campaign-card-toggle"
      >
        <IconDisc icon={Megaphone} tone={tone} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={cn('text-sm font-semibold', inverted && 'text-white')} data-testid="campaign-card-name">
              {name}
            </span>
            {chip && (
              <span
                className={cn(
                  'inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-medium leading-4',
                  inverted ? 'border-white/40 text-white' : 'border-border text-muted-foreground',
                )}
                data-testid="campaign-card-chip"
                data-delivery-channel={card.delivery_channel ?? undefined}
              >
                {chip}
              </span>
            )}
          </div>
          {!open && preview && (
            <div className={cn('truncate text-xs', muted(tone))} data-testid="campaign-card-preview">
              {preview}
            </div>
          )}
        </div>
        {open ? (
          <ChevronDown className={cn('mt-1 h-3.5 w-3.5 flex-shrink-0', muted(tone))} aria-hidden="true" />
        ) : (
          <ChevronRight className={cn('mt-1 h-3.5 w-3.5 flex-shrink-0', muted(tone))} aria-hidden="true" />
        )}
      </button>
      {open && (
        <div
          id={regionId}
          role="region"
          aria-label={t('messaging.mirror.campaign.fullText', 'Campaign message')}
          className={cn('border-l-2 pl-3 text-sm', inverted ? 'border-white/40 text-white' : 'border-border')}
          data-testid="campaign-card-body"
        >
          {card.delivery_channel === 'email' && card.subject && (
            <div className="mb-1 font-medium" data-testid="campaign-card-subject">
              {t('messaging.mirror.campaign.subject', 'Subject: {{subject}}', { subject: card.subject })}
            </div>
          )}
          <div className="whitespace-pre-wrap break-words">{text}</div>
        </div>
      )}
    </div>
  );
}

export function PhoneCallMessageCard({
  card,
  tone = 'default',
  viewer,
  nameOf,
}: {
  card: PhoneCallCard;
  tone?: Tone;
  viewer: Viewer;
  nameOf?: (userId: number) => string | undefined;
}) {
  const { t } = useAppTranslation();
  const inverted = tone === 'inverted';
  // Missed = an inbound call nobody picked up; an unanswered outbound call is "No answer".
  const missed = card.status === 'missed' || (card.direction === 'inbound' && card.status === 'no_answer');
  const Icon = missed ? PhoneMissed : card.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
  const title = missed
    ? t('messaging.mirror.call.missed', 'Missed call')
    : card.direction === 'outbound'
      ? t('messaging.mirror.call.outgoing', 'Outgoing call')
      : card.direction === 'inbound'
        ? t('messaging.mirror.call.incoming', 'Incoming call')
        : t('messaging.mirror.call.phoneCall', 'Phone call');
  const parts: string[] = [];
  if (!missed) {
    if (card.status === 'no_answer') parts.push(t('messaging.mirror.call.noAnswer', 'No answer'));
    else if (card.status === 'busy') parts.push(t('messaging.mirror.call.busy', 'Busy'));
    else if (card.status === 'failed') parts.push(t('messaging.mirror.call.failed', 'Failed'));
  }
  if (card.duration_seconds != null && card.duration_seconds > 0) parts.push(formatCallDuration(card.duration_seconds));
  // Staff name: staff thread only (the member already sees who called on the sender line).
  const staffName =
    viewer === 'staff' ? card.staff_name ?? (card.staff_user_id != null ? nameOf?.(card.staff_user_id) : undefined) : undefined;
  if (staffName) parts.push(staffName);
  return (
    <div
      className="flex items-center gap-3 py-1"
      data-testid="phone-call-card"
      data-direction={card.direction ?? undefined}
      data-status={card.status ?? undefined}
    >
      <IconDisc icon={Icon} tone={tone} alert={missed} />
      <div className="min-w-0">
        <div className={cn('text-sm font-semibold', inverted && 'text-white')} data-testid="phone-call-card-title">
          {title}
        </div>
        {parts.length > 0 && (
          <div className={cn('text-xs', muted(tone))} data-testid="phone-call-card-detail">
            {parts.join(' · ')}
          </div>
        )}
      </div>
    </div>
  );
}

export function VoicemailMessageCard({
  card,
  tone = 'default',
  viewer,
  channelId,
  messageId,
}: {
  card: VoicemailCard;
  tone?: Tone;
  viewer: Viewer;
  channelId?: number | null;
  messageId?: number | null;
}) {
  const { t } = useAppTranslation();
  const inverted = tone === 'inverted';
  // Playback is staff-only and needs the message's ids.
  const canPlay = viewer === 'staff' && card.recording_available;
  const duration = card.duration_seconds != null && card.duration_seconds > 0 ? formatCallDuration(card.duration_seconds) : null;
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid="voicemail-card">
      <div className="flex items-center gap-3 py-1">
        <IconDisc icon={VoicemailIcon} tone={tone} />
        <div className="min-w-0">
          <div className={cn('text-sm font-semibold', inverted && 'text-white')}>
            {t('messaging.mirror.voicemail.title', 'Voicemail')}
          </div>
          {duration && (
            <div className={cn('text-xs', muted(tone))} data-testid="voicemail-card-duration">
              {duration}
            </div>
          )}
        </div>
      </div>
      {canPlay && channelId != null && messageId != null && (
        <VoicemailPlayer channelId={channelId} messageId={messageId} tone={tone} />
      )}
      {/* Transcripts are staff-only (the server strips them for members too). */}
      {viewer === 'staff' &&
        (card.transcript ? (
          <div
            className={cn('whitespace-pre-wrap break-words border-l-2 pl-3 text-sm', inverted ? 'border-white/40 text-white' : 'border-border')}
            data-testid="voicemail-card-transcript"
          >
            {card.transcript}
          </div>
        ) : card.transcript_status === 'pending' ? (
          <div className={cn('text-xs italic', muted(tone))} data-testid="voicemail-card-transcript-pending">
            {t('messaging.mirror.voicemail.transcribing', 'Transcribing…')}
          </div>
        ) : (
          <div className={cn('text-xs italic', muted(tone))} data-testid="voicemail-card-no-transcript">
            {t('messaging.mirror.voicemail.noTranscript', 'Transcript unavailable')}
          </div>
        ))}
    </div>
  );
}

/** "Sent from member profile" under a mirrored outbound SMS / email (staff thread). */
export function MirroredSourceCaption({ source, tone = 'default' }: { source: string; tone?: Tone }) {
  const { t } = useAppTranslation();
  return (
    <span className={cn('text-[11px] italic', muted(tone))} data-testid="mirrored-source" data-source={source}>
      {mirroredSourceText(source, t)}
    </span>
  );
}
